import React, { useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';
import _ from 'lodash';
import { FiSave } from 'react-icons/fi';
import { Breadcrumb, Button, Input, message, Select, Typography } from 'antd';
import { MaskedInput } from 'antd-mask-input';
import { useParams } from 'react-router-dom';

import { ButtonsContainer, Container, SelectOptContainer } from './styles';
import brStates from '../../../../constants/states';
import Meta from '../../../../components/Meta';
import Form from '../../../../components/Form';
import CastersApi from '../../../../services/sdks/caster';
import TextArea from 'antd/lib/input/TextArea';
import { createCasterValidationShape } from '../CreateCaster';
import Fallback from '../../../../components/Fallback';

const updateCasterValidationSchema = Yup.object().shape(
	_.omit(createCasterValidationShape, ['password', 'passwordConfirmation'])
);

const UpdateCaster = () => {
	const [formData, setFormData] = useState({});
	const [errors, setErrors] = useState({});
	const [fallback, setFallback] = useState({ initialData: true });
	const [isEmailTaken, setIsEmailTaken] = useState(false);
	const { casterId } = useParams();

	const handleUpdateCaster = useCallback(async () => {
		setErrors({});

		try {
			const payload = await updateCasterValidationSchema.validate(formData, { abortEarly: false });
			setFallback((prev) => ({ ...prev, updatingCaster: true }));
			await CastersApi.modify(casterId, payload);
			message.success('Locutor atualizado com sucesso');
		} catch (error) {
			setFallback((prev) => ({ ...prev, updatingCaster: false }));

			if (error instanceof Yup.ValidationError) {
				return setErrors(
					Object.values(error.inner).reduce((err, { path }) => ({ ...err, [path]: true }), {})
				);
			}

			if (error.status === 409) {
				setErrors((prev) => ({ ...prev, email: true }));
				return setIsEmailTaken(true);
			}

			message.error('Houve um erro, tente novamente');
		}

		setFallback((prev) => ({ ...prev, updatingCaster: false }));
	}, [formData, casterId]);

	const handleChange = useCallback((e) => {
		e?.persist && e.persist();
		setFormData((prev) => ({
			...prev,
			[e.target.name]: e.target.value,
		}));
	}, []);

	useEffect(() => {
		const fetchCaster = async () => {
			try {
				const {
					data: { caster },
				} = await CastersApi.show(casterId);

				setFormData({
					..._.pick(caster, [
						'name',
						'surname',
						'bio',
						'sex',
						'email',
						'phone',
						'state',
						'city',
						'mic',
						'soundcard',
					]),
					zipCode: String(caster.zipCode),
				});

				setFallback((prev) => ({ ...prev, initialData: false }));
			} catch (error) {
				message.error('Houve um erro ao buscar o locutor, tente novamente');
			}
		};

		if (casterId) {
			fetchCaster();
		}
	}, [casterId]);

	if (fallback?.initialData) {
		return <Fallback title='Carregando' message='Por favor aguarde...' />;
	}

	return (
		<>
			<Meta title='Atualizar Locutor' />

			<Container>
				<Breadcrumb
					style={{ marginBottom: 12 }}
					separator='>'
					routes={[
						{ breadcrumbName: 'INÍCIO' },
						{ breadcrumbName: 'MEUS LOCUTORES' },
						{ breadcrumbName: 'ATUALIZAR LOCUTOR' },
					]}
				/>

				<header>
					<Typography.Title level={2}>Atualizar Locutor</Typography.Title>
				</header>

				<Form.Container layout='1fr 1fr 1fr 1fr 1fr 1fr'>
					<Form.Item size={3} label='Nome' error={errors?.name}>
						<Input
							name='name'
							value={formData.name}
							size='large'
							placeholder='Nome do locutor'
							onChange={handleChange}
						/>
					</Form.Item>
					<Form.Item size={3} label='Sobrenome' error={errors?.surname}>
						<Input
							name='surname'
							value={formData.surname}
							size='large'
							placeholder='Sobrenome do locutor'
							onChange={handleChange}
						/>
					</Form.Item>
					<Form.Item
						size={2}
						label='Email'
						error={errors?.email}
						errorMessage={isEmailTaken ? 'Email já está em uso' : 'Email inválido'}>
						<Input
							name='email'
							value={formData.email}
							size='large'
							placeholder='Email do locutor'
							onChange={handleChange}
						/>
					</Form.Item>
					<Form.Item size={2} label='Sexo' error={errors?.sex}>
						<Select
							size='large'
							value={formData.sex}
							onChange={(value) => handleChange({ target: { name: 'sex', value } })}
							placeholder='Selecione o sexo do locutor'>
							<Select.Option value='MALE'>Masculino</Select.Option>
							<Select.Option value='FEMALE'>Feminino</Select.Option>
						</Select>
					</Form.Item>
					<Form.Item
						size={2}
						label='Telefone'
						error={errors?.phone}
						errorMessage='Telefone inválido'>
						<MaskedInput
							name='phone'
							value={formData.phone}
							size='large'
							autoComplete='off'
							mask='(00) 0 0000-0000'
							placeholder='(__) _ ____-____'
							onChange={handleChange}
						/>
					</Form.Item>

					<Form.Item size={2} label='CEP' error={errors?.zipCode} errorMessage='CEP inválido'>
						<MaskedInput
							name='zipCode'
							value={formData.zipCode}
							size='large'
							autoComplete='off'
							mask='00000-000'
							placeholder='_____-___'
							onChange={handleChange}
						/>
					</Form.Item>
					<Form.Item size={2} label='Estado' error={errors?.state}>
						<Select
							size='large'
							value={formData.state}
							onChange={(value) => handleChange({ target: { name: 'state', value } })}
							placeholder='Selecione o estado'>
							{brStates.map(({ label, value }) => (
								<Select.Option key={value} value={value}>
									<SelectOptContainer>
										<strong>{value}</strong>
										<small>{label}</small>
									</SelectOptContainer>
								</Select.Option>
							))}
						</Select>
					</Form.Item>
					<Form.Item size={2} label='Cidade' error={errors?.city}>
						<Input
							name='city'
							value={formData.city}
							size='large'
							placeholder='Cidade do locutor'
							onChange={handleChange}
						/>
					</Form.Item>

					<Form.Item size={6} label='Descrição do locutor' error={errors?.bio}>
						<TextArea
							name='bio'
							rows={3}
							value={formData.bio}
							size='large'
							placeholder='Descreva o locutor, com suas caracteristicas principais, afinidades, entonação, especialidades e etc...'
							onChange={handleChange}
						/>
					</Form.Item>

					<Form.Item size={3} label='Microfone' error={errors?.mic}>
						<Input
							name='mic'
							value={formData.mic}
							size='large'
							placeholder='Modelo do microfone'
							onChange={handleChange}
						/>
					</Form.Item>
					<Form.Item size={3} label='Placa de som' error={errors?.soundcard}>
						<Input
							name='soundcard'
							value={formData.soundcard}
							size='large'
							placeholder='Modelo da placa de som'
							onChange={handleChange}
						/>
					</Form.Item>
				</Form.Container>

				<ButtonsContainer>
					<Button
						type='primary'
						size='large'
						onClick={handleUpdateCaster}
						loading={fallback?.updatingCaster}>
						<FiSave />
						Atualizar Locutor
					</Button>
				</ButtonsContainer>
			</Container>
		</>
	);
};

export default UpdateCaster;
